// Open Pipe Club - Central API client
// Single source of truth for all backend communication.
// Backend: Cloudflare Worker (D1 + R2). No Trucky integration.

import axios from "axios";
import { normalizeBackendUrl } from "@/config";

const getBackendUrl = () => normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);

const BACKEND_URL = getBackendUrl();
const BASE_URL = `${BACKEND_URL}/api`;

const TOKEN_KEY = "opc_token";

export function getToken() {
  return typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
}

export function setToken(token) {
  if (typeof localStorage !== "undefined") {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }
}

const client = axios.create({ baseURL: BASE_URL });

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function imageUrl(path) {
  if (!path) return "";
  let cleanPath = path;
  if (typeof cleanPath === "string") {
    cleanPath = cleanPath.trim().replace(/^(?:https?:\/\/)?api\.openpipeclub\.com/i, "https://api.openpipeclub.com");
    if (typeof window !== "undefined" && window.location.protocol === "https:" && cleanPath.startsWith("http://")) {
      cleanPath = cleanPath.replace(/^http:\/\//i, "https://");
    }
  }
  let fullUrl = cleanPath.startsWith("http") ? cleanPath : (cleanPath.startsWith("/") ? `${BACKEND_URL}${cleanPath}` : `${BASE_URL}/${cleanPath}`);
  if ((fullUrl.includes("/auth/avatar/") || fullUrl.includes("/auth/banner/")) && !fullUrl.includes("?t=")) {
    const sep = fullUrl.includes("?") ? "&" : "?";
    fullUrl = `${fullUrl}${sep}t=${Math.floor(Date.now() / 60000)}`;
  }
  return fullUrl;
}

const CACHE_PREFIX = "opc_cache_";
const FRESH_TTL_MS = 45 * 1000; // 45 seconds fresh
const STALE_TTL_MS = 5 * 60 * 1000; // 5 minutes stale-while-revalidate

// Ultra-fast in-memory cache for 0ms page transitions
const memoryCache = new Map();
const inFlightRequests = new Map();

const CACHEABLE_ROUTES = [
  "/stats/global",
  "/stats",
  "/stats/member",
  "/events",
  "/news",
  "/gallery",
  "/members",
  "/settings",
  "/applications",
  "/desktop/jobs",
  "/live-map",
];

function isCacheable(method, url) {
  if (method.toUpperCase() !== "GET") return false;
  return CACHEABLE_ROUTES.some(r => url === r || url.startsWith(`${r}/`) || url.startsWith(`${r}?`));
}

export function clearApiCache() {
  memoryCache.clear();
  inFlightRequests.clear();
  if (typeof localStorage === "undefined") return;
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch (e) {}
}

/**
 * Synchronous cache lookup for instantaneous (0ms) component mounting.
 */
export function getCached(url) {
  if (!url) return null;
  const cacheKey = `${CACHE_PREFIX}${url}`;
  
  // 1. In-memory cache (fastest: 0ms)
  const mem = memoryCache.get(cacheKey);
  if (mem && Date.now() - mem.ts < STALE_TTL_MS) {
    return mem.data;
  }

  // 2. LocalStorage fallback
  if (typeof localStorage !== "undefined") {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const item = JSON.parse(raw);
        if (Date.now() - item.ts < STALE_TTL_MS) {
          memoryCache.set(cacheKey, item);
          return item.data;
        }
      }
    } catch (_) {}
  }
  return null;
}

/**
 * Warm up cache in background before user clicks (e.g. on mouse hover).
 */
export function prefetch(url) {
  if (!url) return Promise.resolve(null);
  const cacheKey = `${CACHE_PREFIX}${url}`;
  const mem = memoryCache.get(cacheKey);
  if (mem && Date.now() - mem.ts < FRESH_TTL_MS) {
    return Promise.resolve(mem.data);
  }
  return request("GET", url, null, { isPrefetch: true }).catch(() => null);
}

async function request(method, url, data, config = {}) {
  const normMethod = (method || "GET").toUpperCase();
  const cacheKey = `${CACHE_PREFIX}${url}`;

  // If writing or modifying data, invalidate client cache so fresh state is loaded
  if (normMethod !== "GET") {
    clearApiCache();
  }

  // Check client-side cache for instant 0ms responses on cacheable GET requests
  if (normMethod === "GET" && !config.skipCache && isCacheable(normMethod, url)) {
    // 1. In-memory cache check
    const mem = memoryCache.get(cacheKey);
    if (mem) {
      const age = Date.now() - mem.ts;
      if (age < FRESH_TTL_MS) {
        return Promise.resolve(mem.data);
      }
      if (age < STALE_TTL_MS && !config.isPrefetch) {
        // Stale-While-Revalidate: Return immediately, refresh silently in background
        setTimeout(() => {
          client.request({ method, url, data, ...config })
            .then(res => {
              if (res.status >= 200 && res.status < 300) {
                const updated = { data: res.data, ts: Date.now() };
                memoryCache.set(cacheKey, updated);
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(updated));
                } catch (_) {}
              }
            })
            .catch(() => {});
        }, 0);
        return Promise.resolve(mem.data);
      }
    }

    // 2. LocalStorage check
    try {
      const cached = typeof localStorage !== "undefined" ? localStorage.getItem(cacheKey) : null;
      if (cached) {
        const item = JSON.parse(cached);
        const age = Date.now() - item.ts;
        if (age < FRESH_TTL_MS) {
          memoryCache.set(cacheKey, item);
          return Promise.resolve(item.data);
        }
        if (age < STALE_TTL_MS && !config.isPrefetch) {
          memoryCache.set(cacheKey, item);
          setTimeout(() => {
            client.request({ method, url, data, ...config })
              .then(res => {
                if (res.status >= 200 && res.status < 300) {
                  const updated = { data: res.data, ts: Date.now() };
                  memoryCache.set(cacheKey, updated);
                  try {
                    localStorage.setItem(cacheKey, JSON.stringify(updated));
                  } catch (_) {}
                }
              })
              .catch(() => {});
          }, 0);
          return Promise.resolve(item.data);
        }
      }
    } catch (e) {}

    // 3. In-flight request deduplication
    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey);
    }
  }

  const reqPromise = (async () => {
    try {
      const res = await client.request({ method, url, data, ...config });
      
      // Save successful GET response to in-memory & localStorage cache
      if (normMethod === "GET" && isCacheable(normMethod, url) && res.status >= 200 && res.status < 300) {
        const item = { data: res.data, ts: Date.now() };
        memoryCache.set(cacheKey, item);
        try {
          if (typeof localStorage !== "undefined") {
            localStorage.setItem(cacheKey, JSON.stringify(item));
          }
        } catch (e) {}
      }

      return res.data;
    } catch (err) {
      // Fall back to cached data if present on network error
      if (normMethod === "GET" && isCacheable(normMethod, url)) {
        const mem = memoryCache.get(cacheKey);
        if (mem && mem.data !== undefined) return Promise.resolve(mem.data);
        try {
          const cached = typeof localStorage !== "undefined" ? localStorage.getItem(cacheKey) : null;
          if (cached) {
            const item = JSON.parse(cached);
            if (item && item.data !== undefined) return Promise.resolve(item.data);
          }
        } catch (e) {}
      }

      const message =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Request failed";
      const error = new Error(message);
      error.response = err.response;
      throw error;
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  if (normMethod === "GET" && isCacheable(normMethod, url)) {
    inFlightRequests.set(cacheKey, reqPromise);
  }

  return reqPromise;
}

const api = {
  baseURL: BASE_URL,
  imageUrl,
  getToken,
  setToken,
  clearCache: clearApiCache,
  getCached,
  prefetch,
  request,

  // ───────────── Auth ─────────────
  auth: {
    login: (username, password) => request("POST", "/auth/login", { username, password }),
    register: (payload) => request("POST", "/auth/register", payload),
    me: () => request("GET", "/auth/me"),
    updateProfile: (payload) => request("PUT", "/auth/profile", payload),
    deleteProfile: () => request("DELETE", "/auth/profile"),
    updatePassword: (payload) => request("PUT", "/auth/password", payload),
    uploadAvatar: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return request("POST", "/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    uploadBanner: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return request("POST", "/auth/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
  },

  // ───────────── Members & stats ─────────────
  member: (id) => request("GET", `/members/${id}`),
  memberJobs: (id) => request("GET", `/members/${id}/jobs`),
  members: {
    list: () => request("GET", "/members"),
    get: (id) => request("GET", `/members/${id}`),
    jobs: async (id) => {
      try {
        const localJobs = await request("GET", `/members/${id}/jobs`);
        if (Array.isArray(localJobs)) return localJobs;
      } catch (e) {
        console.debug("[api] Member jobs fetch error:", e);
      }
      return [];
    },
  },
  stats: {
    global: () => request("GET", "/stats/global"),
    member: (id) => request("GET", `/stats/member/${id}`),
  },
  liveMap: () => request("GET", "/live-map"),
  truckersmpServer: (id) => request("GET", `/truckersmp/server/${id}`),

  // ───────────── News ─────────────
  news: {
    list: (params) => request("GET", "/news", null, { params }),
    create: (formData) =>
      request("POST", "/news", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    get: (id) => request("GET", `/news/${id}`),
    update: (id, formData) =>
      request("PUT", `/news/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    remove: (id) => request("DELETE", `/news/${id}`),
    imageUrl: (id) => `${BASE_URL}/news/${id}/image`,
  },

  // ───────────── Gallery ─────────────
  gallery: {
    list: () => request("GET", "/gallery"),
    upload: (formData) =>
      request("POST", "/gallery", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    caption: (id, caption) => request("PUT", `/gallery/${id}/caption`, { caption }),
    remove: (id) => request("DELETE", `/gallery/${id}`),
    imageUrl: (id) => `${BASE_URL}/gallery/image/${id}`,
  },

  // ───────────── Events ─────────────
  events: {
    list: () => request("GET", "/events"),
    create: (formData) =>
      request("POST", "/events", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    get: (id) => request("GET", `/events/${id}`),
    update: (id, formData) =>
      request("PUT", `/events/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    remove: (id) => request("DELETE", `/events/${id}`),
    rsvps: () => request("GET", "/events/rsvps"),
    participants: (id) => request("GET", `/events/${id}/participants`),
    rsvp: (id, payload) => request("POST", `/events/${id}/rsvp`, payload),
    removeRsvp: (id) => request("DELETE", `/events/${id}/rsvp`),
    imageUrl: (id) => `${BASE_URL}/events/${id}/image`,
  },

  // ───────────── Applications & invite codes ─────────────
  applications: {
    getOpen: () => request("GET", "/settings/applications"),
    setOpen: (open) => request("PUT", "/settings/applications", { open }),
    apply: (formData) =>
      request("POST", "/apply", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    list: () => request("GET", "/applications"),
    handle: (id, payload) => request("PUT", `/applications/${id}`, payload),
  },
  inviteCodes: {
    list: () => request("GET", "/invite-codes"),
    create: () => request("POST", "/invite-codes"),
    remove: (code) => request("DELETE", `/invite-codes/${code}`),
  },

  // ───────────── Management ─────────────
  admin: {
    users: () => request("GET", "/admin/users"),
    setRole: (id, role) => request("PUT", `/admin/users/${id}/role?role=${encodeURIComponent(role)}`),
    resetPassword: (id, new_password) => request("PUT", `/admin/users/${id}/password`, { new_password }),
    setTruckersmp: (id, truckersmp_id) => request("PUT", `/admin/users/${id}/truckersmp`, { truckersmp_id }),
    removeTruckersmp: (id) => request("DELETE", `/admin/users/${id}/truckersmp`),
    delete: (id) => request("DELETE", `/admin/users/${id}`),
  },
  settings: {
    get: () => request("GET", "/settings"),
    update: (payload) => request("PUT", "/settings", payload),
  },
  reports: {
    weekly: () => request("GET", "/reports/weekly"),
    list: () => request("GET", "/reports"),
    get: (id) => request("GET", `/reports/${id}`),
    generate: (payload) => request("POST", "/reports/generate", payload),
    delete: (id) => request("DELETE", `/reports/${id}`),
    exportUrl: (id, format = "csv") => `${BASE_URL}/reports/${id}/export?format=${format}`,
  },

  // ───────────── Chat ─────────────
  chat: {
    channels: () => request("GET", "/chat/channels"),
    createChannel: (payload) => request("POST", "/chat/channels", payload),
    createGroup: (payload) => request("POST", "/chat/groups", payload),
    messages: (channelId, since) =>
      request("GET", `/chat/channels/${channelId}/messages`, null, { params: since ? { since } : {} }),
    sendMessage: (channelId, content) => request("POST", `/chat/channels/${channelId}/messages`, { content }),
    addMembers: (channelId, member_ids) => request("POST", `/chat/channels/${channelId}/members`, { member_ids }),
    removeMember: (channelId, user_id) => request("POST", `/chat/channels/${channelId}/remove-member`, { user_id }),
    updateSettings: (channelId, settings) => request("POST", `/chat/channels/${channelId}/settings`, settings),
    leave: (channelId) => request("POST", `/chat/channels/${channelId}/leave`),
    deleteChannel: (channelId) => request("DELETE", `/chat/channels/${channelId}`),
    deleteMessage: (messageId) => request("DELETE", `/chat/messages/${messageId}`),
  },

  // ───────────── Notifications ─────────────
  notifications: {
    list: () => request("GET", "/notifications"),
    read: (id) => request("PUT", `/notifications/${id}/read`),
    readAll: () => request("PUT", "/notifications/read-all"),
  },

  // ───────────── Desktop telemetry / jobs ─────────────
  jobs: {
    list: async () => {
      let localJobs = [];
      try {
        localJobs = await request("GET", "/jobs");
        if (Array.isArray(localJobs) && localJobs.length > 0) return localJobs;
      } catch (e) {}

      try {
        const token = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJjb21wYW55X2lkIjo0MjIyN30.u4DxhyChBtpFLelf4ljd0QM5rDxGPZUvxOJoRVLsRaY";
        const truckyJobs = [];
        for (let page = 1; page <= 8; page++) {
          const res = await fetch(`https://e.truckyapp.com/api/v1/company/42227/jobs?page=${page}&access_token=${token}`);
          if (!res.ok) break;
          const data = await res.json();
          const raw = data.data || [];
          if (raw.length === 0) break;
          raw.forEach(j => {
            truckyJobs.push({
              id: String(j.id || ""),
              username: j.driver?.name || j.driver_name || "",
              driver_name: j.driver?.name || j.driver_name || "",
              source_city: j.source_city_name || "",
              destination_city: j.destination_city_name || "",
              source_company: j.source_company_name || "",
              destination_company: j.destination_company_name || "",
              cargo: j.cargo_name || "",
              driven_distance_km: j.driven_distance_km || j.driven_distance || 0,
              revenue: j.revenue || j.income || 0,
              status: j.status || "completed",
              truck: j.vehicle_brand_name ? `${j.vehicle_brand_name} ${j.vehicle_model_name || ""}` : "",
              is_trucky: true
            });
          });
          if (page >= (data.last_page || 1)) break;
        }
        
        const merged = [...(localJobs || [])];
        const seenIds = new Set(merged.map(j => String(j.id)));
        truckyJobs.forEach(tj => {
          if (!seenIds.has(String(tj.id))) {
            seenIds.add(String(tj.id));
            merged.push(tj);
          }
        });

        if (truckyJobs.length > 0) {
          request("POST", "/jobs/import", { jobs: truckyJobs }).catch(() => {});
        }
        return merged;
      } catch (e) {}

      return localJobs || [];
    },
    position: (payload) => request("POST", "/desktop/position", payload),
    event: (payload) => request("POST", "/desktop/job", payload),
    jobPosition: (payload) => request("POST", "/desktop/job-position", payload),
  },
};

export function calcDriverLevelAndPoints(m) {
  if (!m) return { points: 0, level: 1, progress: 0, ptsInCurrentLevel: 0, nextLevelPoints: 2500 };

  const ms = m.mergedStats || {};

  const dist = Number(m.total_driven_distance_km ?? ms.distance ?? m.distance_km ?? m.distance ?? 0);
  const cargo = Number(m.total_cargo_mass_t ?? ms.cargo ?? m.cargo_mass_t ?? m.cargo ?? 0);
  const jobs = Number(m.total_jobs ?? m.jobs_count ?? ms.jobs ?? m.jobs ?? 0);

  let pts = (m.points !== undefined && m.points !== null)
    ? Number(m.points)
    : ((ms.points !== undefined && ms.points !== null) ? Number(ms.points) : Math.floor(dist + (cargo * 15) + (jobs * 50)));

  let lvl = (m.level !== undefined && m.level !== null)
    ? Number(m.level)
    : ((ms.level !== undefined && ms.level !== null) ? Number(ms.level) : (Math.floor(pts / 2500) + 1));

  if (pts === 0 && (dist > 0 || cargo > 0 || jobs > 0)) {
    pts = Math.floor(dist + (cargo * 15) + (jobs * 50));
    lvl = Math.floor(pts / 2500) + 1;
  }

  const currentLevelPts = (lvl - 1) * 2500;
  const ptsInCurrentLevel = Math.max(0, pts - currentLevelPts);
  const progressPct = Math.min(100, Math.max(0, Math.floor((ptsInCurrentLevel / 2500) * 100)));

  return {
    points: pts,
    level: lvl,
    progress: progressPct,
    ptsInCurrentLevel,
    nextLevelPoints: lvl * 2500
  };
}

export default api;
